import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Input, type InputProps } from '@fluentui/react-components';
import { useLocaleSettings } from '../../hooks/useLocaleSettings';
import type { DecimalSeparator } from '../../utils/locale-format';
import {
  canonicalToNumber, formatNumberInput, parseNumberInput,
} from '../../utils/number-format';

export interface NumberInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type' | 'defaultValue'> {
  /** Valor canónico ("1234.5") o número. Vacío / undefined = sin valor. */
  value: string | number | undefined | null;
  /** Recibe el valor canónico (siempre con "." decimal, sin separadores de miles) y su número. */
  onValueChange: (canonical: string, num: number | undefined) => void;
  allowNegative?: boolean;
  maxDecimals?: number;
}

const toCanonical = (v: string | number | undefined | null): string =>
  v === undefined || v === null ? '' : String(v);

const isSignificant = (c: string, dec: string) => /[0-9]/.test(c) || c === dec || c === '-';

/**
 * Input numérico/monetario (#55): muestra separador de miles y respeta el
 * separador decimal regional (`locale.decimalSeparator`) mientras se teclea,
 * pero entrega al formulario el valor numérico canónico para el submit.
 * Reemplaza a `<Input type="number">` (que no formatea miles).
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { value, onValueChange, allowNegative, maxDecimals, ...rest }, ref,
) {
  const dec: DecimalSeparator = useLocaleSettings().decimalSeparator ?? '.';
  const [text, setText] = useState(() => formatNumberInput(toCanonical(value), dec));
  const innerRef = useRef<HTMLInputElement | null>(null);
  const pendingCaret = useRef<number | null>(null);
  const lastCanonical = useRef(toCanonical(value));
  const lastDec = useRef(dec);

  // Sincroniza desde afuera (reset del formulario, edición) solo si el número realmente cambió,
  // para no pisar lo que el usuario está tecleando ("12." sigue siendo 12).
  useEffect(() => {
    const incoming = toCanonical(value);
    if (canonicalToNumber(incoming) !== canonicalToNumber(lastCanonical.current)) {
      lastCanonical.current = incoming;
      setText(formatNumberInput(incoming, dec));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Si carga la config regional y cambia el separador, reformatea con el nuevo.
  useEffect(() => {
    if (lastDec.current === dec) return;
    const prev = lastDec.current;
    lastDec.current = dec;
    setText((t) => formatNumberInput(parseNumberInput(t, prev, { allowNegative }), dec));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dec]);

  useLayoutEffect(() => {
    if (pendingCaret.current !== null && innerRef.current) {
      const pos = pendingCaret.current;
      pendingCaret.current = null;
      try { innerRef.current.setSelectionRange(pos, pos); } catch { /* no soportado */ }
    }
  });

  const setRefs = (el: HTMLInputElement | null) => {
    innerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = el;
  };

  return (
    <Input
      {...rest}
      ref={setRefs}
      inputMode="decimal"
      value={text}
      onChange={(ev, data) => {
        const el = ev.target as HTMLInputElement;
        const caret = el.selectionStart ?? data.value.length;
        const significantBefore = [...data.value.slice(0, caret)].filter((c) => isSignificant(c, dec)).length;
        const canonical = parseNumberInput(data.value, dec, { allowNegative, maxDecimals });
        const formatted = formatNumberInput(canonical, dec);
        // Caret: tras el mismo número de caracteres significativos que antes de reformatear.
        let seen = 0; let pos = 0;
        while (pos < formatted.length && seen < significantBefore) {
          if (isSignificant(formatted[pos]!, dec)) seen++;
          pos++;
        }
        pendingCaret.current = pos;
        lastCanonical.current = canonical;
        setText(formatted);
        onValueChange(canonical, canonicalToNumber(canonical));
      }}
    />
  );
});
